// The DS2 Numeric fields of a panel keep their own text while focused: only valid numbers are committed while typing,
// and values coming from the props (engine output, patches) do not overwrite what is being typed. On blur the field
// shows the props again; an emptied field commits null (allowEmpty false: it reverts to the last value). One field is
// focused at a time, so one state serves the whole panel: draft(key, value, onChange) gives the props of the Numeric
// with that key.
import { useState } from "react";

type DraftOptions = {
  // numbers that may be committed while typing (others stay as text until blur)
  validate?: (value: number) => boolean;
  allowEmpty?: boolean;
};

export function useNumberDraft() {
  const [editing, setEditing] = useState<{ key: string; value: number | null } | null>(null);

  return (
    key: string,
    value: number | null | undefined,
    onChange: (value: number | null) => void,
    { validate, allowEmpty = true }: DraftOptions = {},
  ) => {
    const shown = value === null || value === undefined || Number.isNaN(value) ? null : value;
    const typing = editing?.key === key ? editing : null;
    return {
      value: typing ? typing.value : shown,
      onFocus: () => setEditing({ key, value: shown }),
      onBlur: () => {
        if (typing && typing.value === null && shown !== null && allowEmpty) onChange(null);
        setEditing(null);
      },
      onValueChange: (next: number | null) => {
        // react-number-format also reports value-prop changes: only user edits pass
        if (!typing) {
          if (next !== shown) onChange(next);
          return;
        }
        if (next === typing.value) return;
        setEditing({ key, value: next });
        if (next !== null && (!validate || validate(next))) onChange(next);
      },
    };
  };
}
